import { useMemo, useState } from "react";
import type { PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import {
  ContributionDialog,
  ContributionsFilters,
  type ContributionsFiltersValue,
} from "@/features/fundraising/components";
import { useFundraisingTranslation } from "@/features/fundraising/i18n/use-fundraising-translation";
import type { TransactionFilter } from "@/features/wallet/api/dto";
import { useTransactions } from "@/features/wallet/api/use-transactions";
import { transactionsColumns } from "@/features/wallet/components";

/**
 * Suivi des collectes / cagnottes (Phase 9) — portées par les types de
 * transaction `POT_CONTRIBUTION` / `POT_WITHDRAWAL` du wallet-service
 * (§6.3) : la liste réutilise le hook et les colonnes de `features/wallet`.
 * Le détail d'une contribution (reçu + polling de statut, §5.3) s'ouvre
 * sur la page détail des transactions via l'action « Voir » des lignes.
 */
export function FundraisingCampaignsPage() {
  const { t } = useFundraisingTranslation();
  const [contributionOpen, setContributionOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [filters, setFilters] = useState<ContributionsFiltersValue>({});

  const params = useMemo(() => {
    const filter: TransactionFilter = {
      type: filters.type ?? "POT_CONTRIBUTION",
    };
    if (filters.status) filter.status = filters.status;
    return {
      filter,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    };
  }, [filters, pagination]);

  const { data, isLoading, isFetching, error, refetch } = useTransactions(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            {t("fundraising.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("fundraising.subtitle")}
          </p>
        </div>
        <Button onClick={() => setContributionOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t("fundraising.create")}
        </Button>
      </div>

      <ContributionsFilters
        value={filters}
        onChange={(value) => {
          setFilters(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
      />

      <DataTableClar
        data={data?.items ?? []}
        columnsClar={transactionsColumns}
        title={t("fundraising.title")}
        exportFileName="fundraising-contributions"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <ContributionDialog
        open={contributionOpen}
        onOpenChange={setContributionOpen}
      />
    </section>
  );
}
